/**
 * IconButton Component
 * Icon-only button with consistent styling and hover states
 */

import React from "react";
import { IconButton as ChakraIconButton, IconButtonProps as ChakraIconButtonProps } from "@chakra-ui/react";
import { Icon, IconTypeToken, IconSizeToken } from "../primitives/Icon";

type IconButtonVariant = "subtle" | "ghost";
type IconButtonSize = "sm" | "md" | "lg" | "base";

export interface IconButtonProps extends Omit<ChakraIconButtonProps, "variant" | "size" | "icon" | "type"> {
  iconType?: IconTypeToken;
  customIcon?: React.ReactElement; // Escape hatch for custom icons
  iconSize?: IconSizeToken;
  "aria-label": string;
  variant?: IconButtonVariant;
  size?: IconButtonSize;
  type?: "button" | "submit" | "reset"; // HTML button type
}

const IconButtonComponent = React.forwardRef<HTMLButtonElement, IconButtonProps>(({
  iconType,
  customIcon,
  iconSize = "base",
  variant = "subtle",
  size = "base",
  type = "button",
  ...props
}, ref) => {
  const getVariantStyles = () => {
    switch (variant) {
      case "subtle":
        return {
          bg: "bg.tertiary",
          color: "content.primary",
          _hover: {
            bg: "bg.secondary",
            color: "content.primary",
          },
        };
      case "ghost":
        return {
          bg: "transparent",
          color: "content.primary",
          _hover: {
            bg: "bg.secondary",
            color: "content.primary",
          },
        };
      default:
        return {};
    }
  };

  const getSizeStyles = () => {
    switch (size) {
      case "sm":
        return {
          minW: "24px",
          h: "24px",
          fontSize: "sm",
        };
      case "md":
      case "base":
        return {
          minW: "32px",
          h: "32px",
          fontSize: "md",
        };
      case "lg":
        return {
          minW: "40px",
          h: "40px",
          fontSize: "lg",
        };
      default:
        return {};
    }
  };

  // Render icon based on iconType or customIcon
  const renderIcon = (): React.ReactElement => {
    if (customIcon) {
      return customIcon;
    }

    if (iconType) {
      return <Icon type={iconType} size={iconSize} />;
    }

    console.warn("IconButton requires either 'iconType' or 'customIcon' prop");
    return <span />;

  };

  return (
    <ChakraIconButton
      ref={ref}
      icon={renderIcon()}
      variant="unstyled"
      type={type}
      {...getVariantStyles()}
      {...getSizeStyles()}
      {...props}
    />
  );
});

IconButtonComponent.displayName = "IconButton";

// Button tokens for consistent usage
const iconButtonTokens = {
  variant: {
    subtle: "subtle" as const,
    ghost: "ghost" as const,
  },
  size: {
    sm: "sm" as const,
    md: "md" as const,
    lg: "lg" as const,
    base: "base" as const,
  },
  // Re-export icon size tokens for convenience
  iconSize: {
    xs: "xs" as IconSizeToken,
    sm: "sm" as IconSizeToken,
    base: "base" as IconSizeToken,
    md: "md" as IconSizeToken,
    lg: "lg" as IconSizeToken,
    xl: "xl" as IconSizeToken,
  },
};

// Public API - combines tokens and component
export const IconButton = Object.assign(IconButtonComponent, iconButtonTokens);
